import { useState, useEffect } from "react";
import { Users, Calendar, ClipboardList, Star } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AdminLayout } from "@/components/AdminLayout";
import { statsAPI, bookingsAPI } from "@/lib/api";
import { toast } from "sonner";
import { format, parse } from "date-fns";
import { ptBR } from "date-fns/locale";

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [recentBookings, setRecentBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, bookingsRes] = await Promise.all([
        statsAPI.get(),
        bookingsAPI.list(),
      ]);
      setStats(statsRes.data);
      setRecentBookings(bookingsRes.data.slice(-5).reverse());
    } catch (error) {
      toast.error("Erro ao carregar dados");
    } finally {
      setIsLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, colorClass, bgClass }) => (
    <Card className={`stat-card ${bgClass} border-0 shadow-md`}>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className="text-3xl font-bold font-nunito mt-1">{value}</p>
          </div>
          <div className={`w-12 h-12 rounded-2xl ${colorClass} flex items-center justify-center`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const formatDate = (dateStr) => {
    try {
      return format(parse(dateStr, "yyyy-MM-dd", new Date()), "dd/MM/yyyy", { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  if (isLoading) {
    return (
      <AdminLayout title="Dashboard">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Dashboard">
      <div className="space-y-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          <StatCard
            title="Crianças Cadastradas"
            value={stats?.total_children || 0}
            icon={Users}
            colorClass="bg-primary"
            bgClass="stat-card-primary"
          />
          <StatCard
            title="Datas Agendadas"
            value={stats?.total_workshops || 0}
            icon={Calendar}
            colorClass="bg-violet-500"
            bgClass="stat-card-robotics"
          />
          <StatCard
            title="Total de Agendamentos"
            value={stats?.total_bookings || 0}
            icon={ClipboardList}
            colorClass="bg-emerald-500"
            bgClass="stat-card-digital"
          />
        </div>

        {/* Recent Bookings */}
        <div className="grid grid-cols-1 gap-6">
          <Card className="border-0 shadow-md">
            <CardHeader>
              <CardTitle className="font-nunito flex items-center gap-2">
                <Calendar className="w-5 h-5 text-primary" />
                Últimos Agendamentos
              </CardTitle>
            </CardHeader>
            <CardContent>
              {recentBookings.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  Nenhum agendamento realizado ainda
                </p>
              ) : (
                <div className="space-y-3">
                  {recentBookings.map((booking) => (
                    <div
                      key={booking.id}
                      className="flex items-center justify-between p-3 rounded-xl bg-muted/50"
                    >
                      <div className="flex items-center gap-3">
                        <Star className="w-5 h-5 text-primary" />
                        <div>
                          <p className="font-medium text-sm">{booking.child_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatDate(booking.workshop_date)} às {booking.workshop_time}
                          </p>
                        </div>
                      </div>
                      <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary font-medium">
                        Evento Completo
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}
